import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Siren, ClipboardList, Send, PhoneCall, RefreshCw, AlertTriangle, Users, UserCheck, MessageSquare,
  Camera, StickyNote, HelpCircle, Clock, Mail, CheckCircle2, Download, Loader2, ShieldAlert, ArrowRight, X, ChevronDown, Voicemail, PhoneIncoming,
} from "lucide-react";
import { ContactedPanel } from "@/components/EmergencyLadderBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fileUrl } from "@/pages/dashboard/ctx";
import { ReporterBlock, AgoChips, fmtDur } from "@/components/EmergencyBits";

const KIND = {
  reported: [Siren, "text-red-600 bg-red-50"],
  ticket: [ClipboardList, "text-ink/70 bg-sand-100"],
  photo_ask: [Camera, "text-ink/70 bg-sand-100"],
  alerted: [Send, "text-sky-700 bg-sky-50"],
  called: [PhoneCall, "text-sky-700 bg-sky-50"],
  retext: [RefreshCw, "text-amber-700 bg-amber-50"],
  last_resort: [AlertTriangle, "text-red-700 bg-red-50"],
  repeat: [RefreshCw, "text-red-700 bg-red-50"],
  exhausted: [ShieldAlert, "text-red-800 bg-red-100"],
  voicemail: [Voicemail, "text-amber-700 bg-amber-50"],
  call_answered: [PhoneIncoming, "text-emerald-700 bg-emerald-50"],
  second_report: [Users, "text-red-600 bg-red-50"],
  acked: [UserCheck, "text-emerald-700 bg-emerald-50"],
  resident_message: [MessageSquare, "text-ink/70 bg-sand-100"],
  photo: [Camera, "text-ink/70 bg-sand-100"],
  note_note: [StickyNote, "text-amber-700 bg-amber-50"],
  note_question: [HelpCircle, "text-amber-700 bg-amber-50"],
  note_resolved: [CheckCircle2, "text-emerald-700 bg-emerald-50"],
  followup: [Clock, "text-ink/70 bg-sand-100"],
  fyi: [Mail, "text-ink/70 bg-sand-100"],
  resolved: [CheckCircle2, "text-emerald-700 bg-emerald-50"],
};

const STATUS_LABEL = { alerting: "Alerting", acknowledged: "Owned", resolved: "Resolved", expired: "Expired" };

const Metric = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-sand-200 bg-white px-3 py-2">
    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink/45">{label}</div>
    <div className="mt-0.5 text-sm font-extrabold text-ink">{value}</div>
  </div>
);

export async function downloadIncidentPdf(tenantId, ev) {
  const r = await api.get(`/tenants/${tenantId}/emergency/events/${ev.id}/incident.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = `incident-${(ev.created_at || "").slice(0, 10)}.pdf`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function IncidentPdfButton({ tenantId, ev, className = "", testid = "emergency-incident-pdf" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await downloadIncidentPdf(tenantId, ev); }
    catch (e) { toast.error(errMsg(e, "Couldn't build the incident report")); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testid} onClick={go} disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink disabled:opacity-50 ${className}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Incident report (PDF)
    </button>
  );
}

// The whole record of one emergency, oldest first, with the two numbers a board asks for.
export function EmergencyTimelineDialog({ tenantId, eventId, onClose, canEdit = true, me = null }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [fixing, setFixing] = useState(false);
  const [fixAt, setFixAt] = useState("");
  const load = () => api.get(`/tenants/${tenantId}/emergency/events/${eventId}/timeline`)
    .then((r) => setData(r.data))
    .catch((e) => setErr(errMsg(e, "Couldn't load the record")));
  useEffect(() => {
    if (!eventId) return;
    setData(null); setErr(""); setFixing(false); setFixAt("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, eventId]);
  const ev = data?.event;
  const m = data?.metrics || {};
  const canFix = canEdit || (!!me && ev?.acked_by?.user_id === me);
  const fmtAt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const lastFix = (ev?.resolved_at_history || []).slice(-1)[0];
  const saveFix = async () => {
    if (!fixAt) return;
    try {
      await api.patch(`/tenants/${tenantId}/emergency/events/${eventId}/resolved-at`, { resolved_at: new Date(fixAt).toISOString() });
      toast.success("Resolution time corrected — the record shows both times");
      setFixing(false); load();
    } catch (e) { toast.error(errMsg(e, "Couldn't correct the time")); }
  };
  const localInput = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
  const fixMins = fixAt && ev ? Math.round((new Date(fixAt).getTime() - new Date(ev.created_at).getTime()) / 60000) : null;
  return (
    <Dialog open={!!eventId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="emergency-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-red-600" /> {ev ? ev.issue : "Emergency record"}
          </DialogTitle>
          <DialogDescription>
            {ev ? <>Reported {data.entries?.[0]?.when} · {STATUS_LABEL[ev.status] || ev.status}{ev.drill ? " · drill" : ""}{ev.ticket_ref ? ` · ${ev.ticket_ref}` : ""}</> : "Every fact on the record, in order."}
          </DialogDescription>
        </DialogHeader>
        {err && <p data-testid="emergency-timeline-error" className="text-sm text-red-700">{err}</p>}
        {!data && !err && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-redline-500" /></div>}
        {ev && (
          <div className="space-y-4">
            <ReporterBlock ev={ev} compact />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric testid="emergency-metric-ack" label="Time to acknowledge" value={m.minutes_to_ack == null ? (ev.status === "resolved" ? "—" : "waiting") : `${m.minutes_to_ack} min`} />
              <Metric testid="emergency-metric-resolve" label="Time to resolve" value={m.minutes_to_resolve == null ? "open" : `${m.minutes_to_resolve} min`} />
              <Metric testid="emergency-metric-alerted" label="Alerted · called" value={`${m.alerted || 0} · ${m.called || 0}`} />
              <Metric testid="emergency-metric-owner" label="Owner" value={ev.acked_by?.name || "nobody"} />
            </div>
            <div data-testid="emergency-record-contacted" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3">
              <ContactedPanel ev={ev} />
            </div>
            <ol data-testid="emergency-timeline" className="relative space-y-3 border-l border-sand-200 pl-5">
              {(data.entries || []).map((e, i) => {
                const [Icon, cls] = KIND[e.kind] || KIND.note_note;
                return (
                  <li key={i} data-testid={`emergency-timeline-entry-${e.kind}`} className="relative">
                    <span className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
                    <div className="text-[11px] text-ink/45">{e.when}{e.actor ? <> · <span className="font-semibold text-ink/70">{e.actor}</span></> : null}</div>
                    <div className="text-sm text-ink">{e.text}</div>
                    {e.kind === "photo" && e.meta?.path && (
                      <a href={fileUrl(e.meta.path)} target="_blank" rel="noreferrer" data-testid="emergency-timeline-photo">
                        <img src={fileUrl(e.meta.path)} alt={e.meta.caption || "Photo from the reporter"} className="mt-1.5 max-h-40 rounded-lg border border-sand-200 hover:opacity-90" />
                      </a>
                    )}
                  </li>
                );
              })}
            </ol>
            {ev.status === "resolved" && (
              <div data-testid="emergency-fix-time" className="rounded-xl border border-dashed border-sand-300 px-3 py-2 text-xs text-ink/60">
                {!fixing ? (
                  <div className="space-y-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span data-testid="emergency-fix-time-summary">
                        Marked resolved {fmtAt(ev.closed_at || ev.resolved_at)}
                        {ev.resolved_at_entered && ev.closed_at && ev.closed_at !== ev.resolved_at && <> · actually over <span className="font-bold text-ink/80">{fmtAt(ev.resolved_at)}</span></>}
                      </span>
                      {canFix && (
                        <button type="button" data-testid="emergency-fix-time-btn" onClick={() => { setFixing(true); setFixAt(localInput(new Date(ev.resolved_at))); }}
                          className="ml-auto font-bold text-redline-600 hover:text-redline-700">{ev.resolved_at_entered ? "Correct the time" : "Marked it late? Correct the time"}</button>
                      )}
                    </span>
                    {lastFix && (
                      <div data-testid="emergency-fix-time-history" className="text-[11px] text-ink/45">
                        Time corrected by {lastFix.by} on {fmtAt(lastFix.at)} (was {fmtAt(lastFix.from)}){(ev.resolved_at_history || []).length > 1 ? ` · ${ev.resolved_at_history.length} corrections` : ""}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <span className="flex flex-wrap items-center gap-2">
                      It was actually resolved at
                      <input data-testid="emergency-fix-time-input" type="datetime-local" value={fixAt} min={localInput(new Date(ev.created_at))} max={localInput(new Date())}
                        onChange={(e) => setFixAt(e.target.value)} className="rounded-lg border border-sand-200 px-2 py-1 text-xs text-ink outline-none focus:border-ink" />
                      <AgoChips minIso={ev.created_at} onPick={setFixAt} testid="emergency-fix-ago" />
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      {fixMins != null && fixMins >= 0 && <span data-testid="emergency-fix-time-preview">Time to resolve becomes <span className="font-bold text-ink/80">{fmtDur(fixMins)}</span> · the moment it was marked stays on the record</span>}
                      <button type="button" data-testid="emergency-fix-time-save" onClick={saveFix} className="ml-auto rounded-full bg-ink px-3 py-1 text-xs font-bold text-white">Save</button>
                      <button type="button" data-testid="emergency-fix-time-cancel" onClick={() => setFixing(false)} className="text-xs font-semibold text-ink/50">Cancel</button>
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sand-100 pt-3">
              <IncidentPdfButton tenantId={tenantId} ev={ev} />
              {ev.ticket_id && (
                <Link to={`/dashboard/actions?ticket=${ev.ticket_id}`} data-testid="emergency-open-ticket" className="inline-flex items-center gap-1 text-xs font-semibold text-redline-600 hover:text-redline-700">
                  Open request {ev.ticket_ref} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Strip above a conversation that turned into an emergency — who, status, owner, one click to the
// record. Red while it's open. Green for the first 24 h after it's resolved (people re-open the thread
// right after to see what happened). Older than that it collapses to a one-line grey chip so the
// history stays one tap away without a wall of green every time the thread is opened.
const RESOLVED_STRIP_HOURS = 24;

const DISMISS_KEY = "askredline.emergencyStrip.dismissed";
const readDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}"); } catch { return {}; } };

export function EmergencyConvBanner({ tenantId, convId }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [showWho, setShowWho] = useState(false);
  useEffect(() => {
    if (!tenantId || !convId) return;
    setData(null);
    const pull = () => api.get(`/tenants/${tenantId}/emergency/for-conversation/${convId}`).then((r) => setData(r.data)).catch(() => setData(null));
    pull();
    const t = setInterval(pull, 20000);   // an open emergency changes by the minute — keep the strip live
    return () => clearInterval(t);
  }, [tenantId, convId]);
  const ev = data?.event;
  if (!ev) return null;
  const open = ev.status !== "resolved";
  const m = data.metrics || {};
  const closedAt = ev.closed_at || ev.resolved_at;
  const ageHours = closedAt ? (Date.now() - new Date(closedAt).getTime()) / 36e5 : 0;
  const resolvedLine = `resolved by ${ev.resolved_by || "the team"}${m.minutes_to_resolve != null ? ` in ${m.minutes_to_resolve} min` : ""}`;
  // The X on a resolved strip collapses it to the chip right away (remembered on this device).
  const dismiss = () => { const next = { ...readDismissed(), [ev.id]: 1 }; try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* private mode */ } setDismissed(next); };

  if (!open && (ageHours > RESOLVED_STRIP_HOURS || dismissed[ev.id])) {
    const when = closedAt ? new Date(closedAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "";
    return (
      <div data-testid="conversation-emergency-chip" className="flex items-center gap-2 border-b border-sand-200 bg-sand-50 px-4 py-1.5 text-[11px] text-ink/60">
        <Siren className="h-3.5 w-3.5 shrink-0 text-ink/40" />
        <span className="min-w-0 truncate"><span className="font-semibold text-ink/70">Emergency{when ? ` on ${when}` : ""}</span> · {ev.issue} · {resolvedLine}</span>
        <Link to={`/dashboard/emergency?event=${ev.id}`} data-testid="conversation-emergency-open-record" className="ml-auto shrink-0 inline-flex items-center gap-1 font-bold text-ink/70 hover:text-ink">
          Open record <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="conversation-emergency-banner"
      className={`border-b text-xs ${open ? "border-red-200 bg-red-50 text-red-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
      <span className="inline-flex items-center gap-1.5 font-extrabold uppercase tracking-wide">
        <Siren className="h-3.5 w-3.5" /> {open ? "Emergency" : "Emergency resolved"}
      </span>
      <span className="font-semibold">{ev.issue}</span>
      {open && <span className="text-ink/60">· {data.reporter_line}</span>}
      <span className="text-ink/60">
        · {ev.status === "alerting" ? `${m.alerted || 0} alerted, nobody has taken it yet`
          : ev.status === "acknowledged" ? `${ev.acked_by?.name} owns it`
          : resolvedLine}
      </span>
      <span className="ml-auto inline-flex items-center gap-1">
        {open && (
          <button type="button" data-testid="conversation-emergency-who" onClick={() => setShowWho((v) => !v)} aria-expanded={showWho}
            className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-1 font-bold hover:bg-white">
            Who's been contacted ({(ev.alerts || []).length}) <ChevronDown className={`h-3 w-3 transition-transform ${showWho ? "rotate-180" : ""}`} />
          </button>
        )}
        <Link to={`/dashboard/emergency?event=${ev.id}`} data-testid="conversation-emergency-open-record"
          className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-1 font-bold hover:bg-white">
          Open record <ArrowRight className="h-3 w-3" />
        </Link>
        {!open && (
          <button type="button" data-testid="conversation-emergency-dismiss" onClick={dismiss} aria-label="Dismiss"
            className="-my-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-emerald-900/70 hover:bg-white/80 hover:text-emerald-900 active:bg-white"><X className="h-4 w-4" strokeWidth={2.5} /></button>
        )}
      </span>
    </div>
    {open && showWho && (
      <div className="border-t border-red-200/70 px-4 py-2.5" data-testid="conversation-emergency-contacted">
        <ContactedPanel ev={ev} compact />
      </div>
    )}
    </div>
  );
}
